import { ReactNode, useEffect, useState } from 'react';
import { FiBarChart2, FiBox, FiCreditCard, FiGrid, FiLogOut, FiMenu, FiMonitor, FiPackage, FiSettings,
  FiShoppingCart, FiTag, FiUsers, FiFileText } from 'react-icons/fi';
import { NavLink, useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { I18nKey, t } from '../i18n';
import api from '../services/api';
import { ROLE_LABEL, type Role } from '../types';
import { formatINR } from '../utils/money';

interface NavItem { to: string; key: I18nKey; icon: ReactNode; roles: Role[]; feature?: string }

const NAV: NavItem[] = [
  { to: '/dashboard', key: 'nav.dashboard', icon: <FiGrid />, roles: ['owner', 'manager'] },
  { to: '/billing', key: 'nav.billing', icon: <FiShoppingCart />, roles: ['owner', 'manager', 'cashier'] },
  { to: '/products', key: 'nav.products', icon: <FiPackage />, roles: ['owner', 'manager', 'cashier'] },
  { to: '/stock', key: 'nav.stock', icon: <FiBox />, roles: ['owner', 'manager'] },
  { to: '/labels', key: 'nav.labels', icon: <FiTag />, roles: ['owner', 'manager'], feature: 'label_printing' },
  { to: '/sales', key: 'nav.sales', icon: <FiFileText />, roles: ['owner', 'manager', 'cashier'] },
  { to: '/reports', key: 'nav.reports', icon: <FiBarChart2 />, roles: ['owner', 'manager'] },
  { to: '/counters', key: 'nav.counters', icon: <FiMonitor />, roles: ['owner', 'manager'] },
  { to: '/users', key: 'nav.users', icon: <FiUsers />, roles: ['owner'] },
  { to: '/subscription', key: 'nav.subscription', icon: <FiCreditCard />, roles: ['owner'] },
  { to: '/settings', key: 'nav.settings', icon: <FiSettings />, roles: ['owner'] },
];

function SubscriptionBanner() {
  const { session, isRole } = useAuth();
  const sub = session?.subscription;
  if (!sub || !isRole('owner') || !['reminder', 'grace'].includes(sub.state)) return null;
  const overdue = sub.state === 'grace';
  return (
    <div className={`px-4 py-2 text-sm ${overdue ? 'bg-red-600 text-white' : 'bg-amber-100 text-amber-900'} print:hidden`}>
      {overdue
        ? <>Subscription payment overdue ({formatINR(sub.amount_due_paise)}). Billing stops after <b>{sub.pay_by}</b>. </>
        : <>Subscription due on <b>{sub.due_date}</b> ({sub.days_until_due} days). </>}
      <NavLink to="/subscription" className="font-semibold underline">Pay now</NavLink>
    </div>
  );
}

export default function Layout({ children }: { children: ReactNode }) {
  const { session, logout, has, isRole } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Counter heartbeat: drives the owner's live "Counters" view.
  useEffect(() => {
    if (!session?.counter || session.read_only) return;
    const beat = () => api.post('/counters/heartbeat').catch(() => undefined);
    beat();
    const id = setInterval(beat, 20000);
    return () => clearInterval(id);
  }, [session?.counter, session?.read_only]);

  if (!session) return null;
  const items = NAV.filter((n) => isRole(...n.roles) && (!n.feature || has(n.feature)));

  return (
    <div className="flex h-full flex-col">
      {session.read_only && (
        <div className="bg-violet-700 px-4 py-1.5 text-center text-sm text-white print:hidden">
          Support mode (read-only) — {session.shop.name}. Changes are disabled.
        </div>
      )}
      <SubscriptionBanner />
      <div className="flex min-h-0 flex-1">
        <aside className={`${open ? 'flex' : 'hidden'} fixed inset-y-0 left-0 z-40 w-60 flex-col bg-slate-900 text-slate-200 md:static md:flex print:hidden`}>
          <div className="border-b border-slate-800 px-4 py-4">
            <div className="text-lg font-bold text-white">Zentrix POS</div>
            <div className="truncate text-sm text-slate-400" title={session.shop.name}>{session.shop.name}</div>
          </div>
          <nav className="flex-1 overflow-y-auto py-2">
            {items.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 text-[15px] ${isActive ? 'bg-brand-700 text-white' : 'hover:bg-slate-800'}`}>
                {n.icon}{t(n.key)}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-slate-800 p-3 text-sm">
            <NavLink to="/profile" onClick={() => setOpen(false)} className="block text-white hover:underline" title="My profile">{session.user.full_name}</NavLink>
            <NavLink to="/profile" onClick={() => setOpen(false)} className="block text-xs text-brand-200 hover:text-white">My profile · change password</NavLink>
            <div className="text-slate-400">{ROLE_LABEL[session.user.role] || session.user.role}{session.counter ? ` · ${session.counter.name}` : ' · Back office'}</div>
            <button className="mt-2 flex items-center gap-2 text-slate-300 hover:text-white"
              onClick={async () => { await logout(); navigate('/login'); }}>
              <FiLogOut /> {t('common.signOut')}
            </button>
          </div>
        </aside>
        <main className="min-w-0 flex-1 overflow-y-auto">
          <button className="btn-ghost m-2 md:hidden print:hidden" onClick={() => setOpen(!open)} aria-label="Menu"><FiMenu size={22} /></button>
          {children}
        </main>
      </div>
    </div>
  );
}
